'use client'

/**
 * Segment analytics supports two modes depending on OneTrust configuration:
 *
 * 1. **No OneTrust data domain script env** (NEXT_PUBLIC_ONE_TRUST_DATA_DOMAIN_SCRIPT unset):
 *    We keep the current behavior: track everything and do not show a consent banner.
 *    Analytics loads immediately with the write key and plugins.
 *
 * 2. **OneTrust data domain script env set**:
 *    We still track everything, but we show the consent banner and integrate Segment
 *    with OneTrust via the consent wrapper. The analytics instance is created empty
 *    first, then loaded through withOneTrust() so consent preferences are respected
 *    and the global `analytics` is exposed for OneTrust's OptanonWrapper.
 */

import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust'
import { AnalyticsBrowser } from '@segment/analytics-next'
import { usePathname } from 'next/navigation'
import { createContext, useContext, useEffect, useRef } from 'react'
import { defaultParametersPlugin } from '../plugins/default-parameters'
import { pageviewIdPlugin } from '../plugins/pageview-id'

const AnalyticsContext = createContext<AnalyticsBrowser | null>(null)

export const SegmentAnalyticsProvider = ({
  children,
  writeKey,
  oneTrustDataDomainScript,
}: {
  children: React.ReactNode
  writeKey: string
  oneTrustDataDomainScript?: string
}) => {
  const pathname = usePathname()
  const analyticsRef = useRef<AnalyticsBrowser | null>(null)
  const loadedRef = useRef<boolean>(false)
  const pageviewIdRef = useRef<string>(crypto.randomUUID())

  if (!analyticsRef.current) {
    analyticsRef.current = oneTrustDataDomainScript
      ? new AnalyticsBrowser()
      : AnalyticsBrowser.load({
          writeKey,
          plugins: [pageviewIdPlugin(() => pageviewIdRef.current), defaultParametersPlugin()],
        })
  }

  useEffect(() => {
    pageviewIdRef.current = crypto.randomUUID()
  }, [pathname])

  useEffect(() => {
    if (oneTrustDataDomainScript && analyticsRef.current && !loadedRef.current) {
      withOneTrust(analyticsRef.current).load({
        writeKey,
        plugins: [pageviewIdPlugin(() => pageviewIdRef.current), defaultParametersPlugin()],
      })

      // Expose Segment instance globally so OneTrust OptanonWrapper can call analytics.track() on `OptanonWrapper` function
      window.analytics = analyticsRef.current

      loadedRef.current = true
    }
  }, [oneTrustDataDomainScript, writeKey])

  return <AnalyticsContext.Provider value={analyticsRef.current}>{children}</AnalyticsContext.Provider>
}

export const useSegmentAnalytics = () => {
  const analytics = useContext(AnalyticsContext)

  if (!analytics) {
    throw new Error('useSegmentAnalytics must be used within a SegmentAnalyticsProvider')
  }

  return analytics
}
